import React, { useEffect, useState } from 'react';
import { sendClientPriceList, ClientPriceList } from '../lib/clientPriceListApi';
import { getClientPriceListPublicLink } from '../lib/clientPriceListLink';
import { EMAIL_WENT_NOTICE_MS, emailAlreadyWent, emailRefusalMessage } from '../lib/emailRefusal';
import { useToast } from '../context/ToastContext';
import { buildDefaultClientPriceListEmailDescription } from '../utils/clientPriceListEmailTemplate';
import { formatDate } from '../utils/dateFormat';
import EmailDraftFields from './EmailDraftFields';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface ClientPriceListEmailDialogProps {
  open: boolean;
  list: ClientPriceList | null;
  onClose: () => void;
  /** Called after a successful send so the caller can refresh (status flips to Sent server-side). */
  onSent: () => void | Promise<void>;
  /** The mail went but could not be recorded (D180): the dialog has closed, the caller re-reads the list. */
  onWentNotRecorded: () => void | Promise<void>;
  /** Bump above another open modal (e.g. the items popup on ClientPriceListsPage). */
  zIndexClassName?: string;
}

/**
 * Shared "Send Price List" compose dialog for both ClientPriceListDetailPage and
 * ClientPriceListsPage — single implementation so the email template, the client
 * link, and the send behavior can't drift between the two pages.
 */
export default function ClientPriceListEmailDialog({
  open,
  list,
  onClose,
  onSent,
  onWentNotRecorded,
  zIndexClassName = 'z-50',
}: ClientPriceListEmailDialogProps) {
  const toast = useToast();
  const [recipientEmail, setRecipientEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [subjectError, setSubjectError] = useState('');
  const [bodyError, setBodyError] = useState('');
  const [sending, setSending] = useState(false);

  // The client's link carries the list's secret (D116.1), which only the staff
  // `/details` read returns: `list` must come from there, never from a list row.
  const token = list?.accessToken;

  useEffect(() => {
    if (!open || !list || !token) return;
    setRecipientEmail('');
    setSubject('');
    setSubjectError('');
    setBodyError('');
    setBody(
      buildDefaultClientPriceListEmailDescription({
        clientName: list.clientName || list.name,
        link: getClientPriceListPublicLink(list._id, token),
      })
    );
  }, [open, list, token]);

  if (!open || !list) return null;

  const link = token ? getClientPriceListPublicLink(list._id, token) : '';

  const handleRestoreDefault = () => {
    if (!link) return;
    setBody(buildDefaultClientPriceListEmailDescription({ clientName: list.clientName || list.name, link }));
  };

  // sentAt also means "Copy Client Link" shared it, so the warning says "sent or shared".
  const alreadySent = !!list.sentAt;

  const handleClose = () => {
    if (sending) return;
    onClose();
  };

  const handleSend = async () => {
    if (!token) {
      alert('This price list has no client link yet. Close this window, reopen the list and try again.');
      return;
    }
    const missingSubject = !subject.trim();
    const missingBody = !body.trim();
    setSubjectError(missingSubject ? 'Enter a subject.' : '');
    setBodyError(missingBody ? 'The email text is empty. Write it, or restore the default.' : '');
    if (missingSubject || missingBody) return;
    if (!recipientEmail.trim() || !EMAIL_RE.test(recipientEmail.trim())) {
      alert('Please enter a valid recipient email address.');
      return;
    }
    setSending(true);
    try {
      await sendClientPriceList(list._id, {
        recipients: recipientEmail.trim(),
        subject,
        body,
        ...(alreadySent ? { resend: true as const } : {}),
      });
      onClose();
      await onSent();
    } catch (e) {
      console.error('Failed to send price list:', e);
      const words = emailRefusalMessage(e) ?? (e instanceof Error ? e.message : 'Failed to send price list. Please check the recipient email and try again.');
      if (emailAlreadyWent(e)) {
        toast.error(words, EMAIL_WENT_NOTICE_MS);
        onClose();
        await onWentNotRecorded();
      } else {
        alert(words);
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className={`fixed inset-0 ${zIndexClassName} flex items-center justify-center bg-[#222222]/48 p-4 modal-overlay-safe`}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        className="bg-white rounded-lg shadow-lg w-full max-w-[610px] max-h-[90vh] flex flex-col overflow-hidden"
        style={{ fontFamily: "'Public Sans', sans-serif", borderRadius: 8 }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 pt-6 pb-2">
          <div>
            <h2 className="text-base font-semibold leading-6 text-slate-900">
              Send Price List
            </h2>
            <p className="text-sm leading-[22px] text-slate-500 mt-1">
              Name: {list.name} | Total Selected Items: {list.items?.length ?? 0}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="w-6 h-6 flex items-center justify-center rounded text-slate-400 hover:bg-[#F9FAF9]"
            aria-label="Close"
          >
            <span className="text-xl leading-none">×</span>
          </button>
        </div>

        <div className="px-6 py-4 space-y-5 overflow-y-auto">
          <div>
            <label className="block font-medium text-slate-900 mb-1.5" style={{ fontSize: 14, lineHeight: '22px' }}>
              Recipient Email
            </label>
            <input
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              placeholder="client@example.com"
              className="w-full h-10 px-3 border border-[#D7DBD9] rounded-md text-base leading-6 placeholder:text-slate-400 text-slate-900 focus:outline-none focus:ring-1 focus:ring-[#D7DBD9]"
            />
          </div>
          <div>
            <EmailDraftFields
              subject={subject}
              onSubjectChange={setSubject}
              body={body}
              onBodyChange={setBody}
              attachment={`Price list ${list.name} (Excel)`}
              subjectError={subjectError}
              bodyError={bodyError}
              disabled={sending}
              onRestoreDefault={handleRestoreDefault}
            />
            {link && !body.includes(link) && (
              <p className="text-sm text-amber-700">The online link is not in this text, so the client will not get it.</p>
            )}
            {alreadySent && (
              <p className="mt-2 text-sm text-amber-700">Sent or shared on {formatDate(list.sentAt)} — sending again will resend.</p>
            )}
          </div>
        </div>

        <div className="px-6 pb-6 pt-2">
          <button
            type="button"
            onClick={handleSend}
            disabled={sending}
            className="w-full h-10 flex items-center justify-center rounded-lg font-semibold text-white bg-primary-500 hover:opacity-90 disabled:opacity-50"
            style={{ fontSize: 16, lineHeight: '24px', borderRadius: 8 }}
          >
            {sending ? 'Sending...' : alreadySent ? 'Send again' : 'Send price list'}
          </button>
        </div>
      </div>
    </div>
  );
}
